import React from 'react';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import PageHeading from '../PageHeading';

class InventoryAdmin extends React.Component {
  constructor() {
    super();
    this.state = {
      products: [],
      drafts: {},
      lowStockThreshold: 10,
      loading: true,
      savingId: null,
      error: null,
      success: null,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
  }

  componentDidMount() {
    if (this.props.isAdmin) {
      this.loadInventory();
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isAdmin && this.props.isAdmin) {
      this.loadInventory();
    }
  }

  async loadInventory() {
    this.setState({ loading: true, error: null });
    try {
      const { data } = await axios.get('/api/admin/inventory');
      const drafts = {};
      (data.products || []).forEach((product) => {
        drafts[product.id] = String(product.inventory);
      });
      this.setState({
        products: data.products || [],
        drafts,
        lowStockThreshold: data.lowStockThreshold,
        loading: false,
      });
    } catch (err) {
      const message =
        (err.response && err.response.data) ||
        err.message ||
        'Failed to load inventory.';
      this.setState({ loading: false, error: message });
    }
  }

  handleChange(productId, value) {
    this.setState((state) => ({
      drafts: { ...state.drafts, [productId]: value },
      success: null,
    }));
  }

  async handleSave(productId) {
    const raw = this.state.drafts[productId];
    const inventory = parseInt(raw, 10);
    if (Number.isNaN(inventory) || inventory < 0) {
      this.setState({ error: 'Inventory must be a non-negative whole number.' });
      return;
    }

    this.setState({ savingId: productId, error: null, success: null });
    try {
      const { data } = await axios.patch(`/api/admin/inventory/${productId}`, {
        inventory,
      });
      this.setState((state) => ({
        products: state.products.map((product) =>
          product.id === productId ? { ...product, ...data } : product
        ),
        drafts: { ...state.drafts, [productId]: String(data.inventory) },
        savingId: null,
        success: `Updated ${data.name} to ${data.inventory}.`,
      }));
    } catch (err) {
      const message =
        (err.response && err.response.data) ||
        err.message ||
        'Failed to update inventory.';
      this.setState({ savingId: null, error: message });
    }
  }

  render() {
    const { isLoggedIn, isAdmin } = this.props;
    const {
      products,
      drafts,
      lowStockThreshold,
      loading,
      savingId,
      error,
      success,
    } = this.state;

    if (!isLoggedIn) {
      return <Redirect to="/login" />;
    }

    if (!isAdmin) {
      return (
        <Container className="pad-t Center-column">
          <PageHeading icon="boxes">Inventory</PageHeading>
          <p className="Words">You do not have access to this page.</p>
          <Link to="/" className="Auth-link">
            Back home
          </Link>
        </Container>
      );
    }

    return (
      <Container className="pad-t Center-column inventory-admin">
        <PageHeading icon="boxes">Inventory</PageHeading>
        <p className="Words inventory-admin-note">
          Low-stock email fires at {lowStockThreshold} units (and again at 0).
        </p>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {loading ? (
          <Spinner animation="border" role="status" />
        ) : (
          <div className="inventory-admin-list">
            {products.map((product) => {
              const draft = drafts[product.id];
              const current = product.inventory;
              const low =
                current > 0 && current <= lowStockThreshold;
              const soldOut = current <= 0;
              return (
                <div key={product.id} className="inventory-admin-row">
                  <div className="inventory-admin-meta">
                    <span className="inventory-admin-name">{product.name}</span>
                    <span
                      className={
                        soldOut
                          ? 'inventory-admin-status sold-out'
                          : low
                            ? 'inventory-admin-status low'
                            : 'inventory-admin-status ok'
                      }
                    >
                      {soldOut
                        ? 'Sold out'
                        : low
                          ? 'Low stock'
                          : 'In stock'}
                      {' · '}
                      {current} on hand
                    </span>
                  </div>
                  <Form
                    className="inventory-admin-form"
                    onSubmit={(evt) => {
                      evt.preventDefault();
                      this.handleSave(product.id);
                    }}
                  >
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      value={draft}
                      onChange={(evt) =>
                        this.handleChange(product.id, evt.target.value)
                      }
                      aria-label={`${product.name} inventory`}
                    />
                    <Button
                      variant="success"
                      type="submit"
                      disabled={savingId === product.id}
                    >
                      {savingId === product.id ? 'Saving…' : 'Save'}
                    </Button>
                  </Form>
                </div>
              );
            })}
            {!products.length && (
              <p className="Words">No products found.</p>
            )}
          </div>
        )}
      </Container>
    );
  }
}

const mapState = (state) => ({
  isLoggedIn: !!state.user.id,
  isAdmin: !!state.user.isAdmin,
});

export default connect(mapState)(InventoryAdmin);

InventoryAdmin.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};
